/**
 * Form validation and handling
 */
import { showToast } from '../utils/toast-utils.js';

export function initializeFormHandlers() {
  // Only handle generic forms, not specific upload forms
  // Audio uploads are handled by audio-upload.js
  // Image uploads are handled by image-upload.js

  // Handle forgot password form
  const forgotPasswordForm = document.querySelector('form[action*="forgot-password"]');
  if (forgotPasswordForm) {
    forgotPasswordForm.addEventListener('submit', function (e) {
      const email = this.querySelector('input[type="email"]').value;
      if (!email) {
        e.preventDefault();
        showToast('Please enter your email address', 'error', { dataTest: 'forgot-password.forgot-password-form.email-error-toast' });
      }
    });
  }

  // Handle password reset form
  const resetPasswordForm = document.querySelector('form[action*="reset-password"]');
  if (resetPasswordForm) {
    resetPasswordForm.addEventListener('submit', function (e) {
      const password = this.querySelector('input[name="password"]').value;
      const password2 = this.querySelector('input[name="password2"]').value;

      if (password !== password2) {
        e.preventDefault();
        showToast('Passwords do not match', 'error', { dataTest: 'reset-password.reset-password-form.password-mismatch-error-toast' });
        return;
      }

      if (password.length < 14) {
        e.preventDefault();
        showToast('Password must be at least 14 characters long', 'error', { dataTest: 'reset-password.reset-password-form.password-length-error-toast' });
        return;
      }
    });
  }

  // Form validation for user management
  const userForm = document.querySelector('form[action*="users"]');
  if (userForm) {
    userForm.addEventListener('submit', function (e) {
      const username = this.querySelector('input[name="username"]');
      const email = this.querySelector('input[name="email"]');
      const password = this.querySelector('input[name="password"]');
      const password2 = this.querySelector('input[name="password2"]');

      if (username && username.value.length < 3) {
        e.preventDefault();
        showToast('Username must be at least 3 characters long', 'error', { dataTest: 'user-management.user-form.username-error-toast' });
        return;
      }

      if (email && !email.value.includes('@')) {
        e.preventDefault();
        showToast('Please enter a valid email address', 'error', { dataTest: 'user-management.user-form.email-error-toast' });
        return;
      }

      if (password && password.value && password.value.length < 14) {
        e.preventDefault();
        showToast('Password must be at least 14 characters long', 'error', { dataTest: 'user-management.user-form.password-length-error-toast' });
        return;
      }

      if (password && password2 && password.value !== password2.value) {
        e.preventDefault();
        showToast('Passwords do not match', 'error', { dataTest: 'user-management.user-form.password-match-error-toast' });
        return;
      }
    });
  }

  // Enhanced form validation feedback
  const forms = document.querySelectorAll('form');
  forms.forEach((form) => {
    form.addEventListener('submit', function (e) {
      const requiredFields = this.querySelectorAll('[required]');
      let isValid = true;

      requiredFields.forEach((field) => {
        if (!field.value.trim()) {
          field.classList.add('is-invalid');
          isValid = false;
        } else {
          field.classList.remove('is-invalid');
        }
      });

      if (!isValid) {
        e.preventDefault();
        const firstInvalid = this.querySelector('.is-invalid');
        if (firstInvalid) {
          firstInvalid.focus();
        }
      }
    });
  });
}

// File upload validation is now handled by specialized upload handlers:
// - Audio uploads: audio-upload.js
// - Image uploads: image-upload.js

// Auto-save functionality for forms
export function initializeAutoSave() {
  const autoSaveForms = document.querySelectorAll('[data-auto-save]');
  autoSaveForms.forEach((form) => {
    const inputs = form.querySelectorAll('input, textarea, select');
    inputs.forEach((input) => {
      input.addEventListener('input', () => {
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        localStorage.setItem('autosave_' + (form.id || 'form'), JSON.stringify(data));
      });
    });

    // Restore saved data on page load
    const savedData = localStorage.getItem('autosave_' + (form.id || 'form'));
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        Object.keys(data).forEach((key) => {
          const input = form.querySelector('[name="' + key + '"]');
          if (input && input.value === '') {
            input.value = data[key];
          }
        });
      } catch (e) {
        console.error('Error restoring form data:', e);
      }
    }
  });
}

// Real-time character count for textareas
export function initializeCharacterCount() {
  const textareas = document.querySelectorAll('textarea[maxlength]');
  textareas.forEach((textarea) => {
    const maxLength = textarea.getAttribute('maxlength');
    const counter = document.createElement('small');
    const initialLength = textarea.value.length;
    counter.className = initialLength > maxLength * 0.9 ? 'form-text text-warning' : 'form-text text-muted';
    counter.textContent = initialLength + '/' + maxLength + ' characters';
    textarea.parentNode.appendChild(counter);

    textarea.addEventListener('input', function () {
      const currentLength = this.value.length;
      counter.textContent = currentLength + '/' + maxLength + ' characters';
      counter.className = currentLength > maxLength * 0.9 ? 'form-text text-warning' : 'form-text text-muted';
    });
  });
}

// Progress tracking for file uploads
export function initializeUploadProgress() {
  const uploadForms = document.querySelectorAll('form[enctype="multipart/form-data"]');
  uploadForms.forEach((form) => {
    form.addEventListener('submit', function (e) {
      const fileInput = this.querySelector('input[type="file"]');
      if (fileInput && fileInput.files.length > 0) {
        const submitBtn = this.querySelector('button[type="submit"]');
        if (submitBtn) {
          submitBtn.disabled = true;

          submitBtn.innerHTML = '<i data-lucide="loader-2" class="lucide-spin me-1" ></i> Uploading...';
          if (window.lucide) window.lucide.createIcons();
        }

        // Create progress bar
        const progressContainer = document.createElement('div');
        progressContainer.className = 'mt-3';
        progressContainer.innerHTML =
          '<div class="progress"><div class="progress-bar" role="progressbar" style="width: 0%"></div></div>';
        this.appendChild(progressContainer);
      }
    });
  });
}
